import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { SelectItem, ConfirmationService, Message } from 'primeng/api';
import { GlobalNamespace as _GlobalNS } from '../../../environments/environment';
import { Person } from '../../person/models/person';
import { Security, SecurityQuestionType, SecurityService } from '../../security';
import { Address, EmailOptions, Phone, CommunicationService } from '../../communication';
import { PersonService } from '../../person/person.service';
import { CustomFormValidator, CustomEmail, CustomPhone, CustomZip } from '../../shared';
import { Subscription, forkJoin } from 'rxjs';
import { GlobalService } from '../../core';

@Component({
  selector: 'app-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss'],
  providers: [ConfirmationService]
})

export class EditProfileComponent implements OnInit, OnDestroy {
  person: Person;
  security: Security;
  address: Address[];
  emailOptions: EmailOptions;
  phone: Phone[];
  questions: SecurityQuestionType;
  states: SelectItem[];
  formErrors = {};
  subscription: Subscription;
  customValidator = new CustomFormValidator();
  questionOptions: SelectItem[];
  numberofQuestions = 2;
  messages: Message[] = [];
  loading = false;
  isPreReg: boolean;
  rejectConfirmation = true;

  profileForm = this.fb.group({
    PersonId: [this._global.find((<any>_GlobalNS).paths.PersonId)],
    Email: [''],
    IsPreReg: [''],
    FirstName: ['', [Validators.required, Validators.maxLength(30)]],
    LastName: ['', [Validators.required, Validators.maxLength(30)]],
    Password: ['', [Validators.minLength(8), Validators.maxLength(50), this.customValidator.passwordComplexity, this.customValidator.mustNotContain('Email'), this.customValidator.mustNotContain('FirstName'), this.customValidator.mustNotContain('LastName')]],
    ConfirmPassword: ['', [this.customValidator.sameAs('Password')]],
    Question1: ['', [this.customValidator.depends('Question2')]],
    Answer1: ['', [Validators.maxLength(50), this.customValidator.depends('Question1')]],
    Question2: ['', [this.customValidator.notSameAs('Question1'), this.customValidator.depends('Question1')]],
    Answer2: ['', [Validators.maxLength(50), this.customValidator.depends('Question2')]],
    Company: ['', [Validators.maxLength(50)]],
    WorkAddress1: ['', [Validators.required, Validators.maxLength(50)]],
    WorkAddress2: ['', [Validators.maxLength(50)]],
    WorkCity: ['', [Validators.required, Validators.maxLength(50)]],
    WorkState: ['', [Validators.required, Validators.maxLength(2)]],
    WorkPostalCode: ['', [Validators.required, Validators.maxLength(10), CustomZip.validate]],
    WorkPhone: ['', [Validators.required, CustomPhone.validate]],
    WorkMobile: ['', [CustomPhone.validate]],
    WorkFax: ['', [CustomPhone.validate]],
    SameAsWork: ['', []],
    /*
    // commenting out this code as it may be required in future
    HomeAddress1: ['', [Validators.maxLength(50)]],
    HomeAddress2: ['', [Validators.maxLength(50)]],
    HomeCity: ['', [Validators.maxLength(50)]],
    HomeState: ['', [Validators.maxLength(2)]],
    HomePostalCode: ['', [Validators.maxLength(10), CustomZip.validate]],
    HomePhone: ['', [CustomPhone.validate]],
    HomeMobile: ['', [CustomPhone.validate]],
    HomeFax: ['', [CustomPhone.validate]],
    */

    Signature: ['', [Validators.maxLength(500)]],
    OOOIsSelected: ['', [Validators.required]],
    Message: ['', [Validators.maxLength(500)]],
    CCIsSelected: ['', [Validators.required]],
    CCEmail: ['', [Validators.maxLength(100), CustomEmail.validate, this.customValidator.depends('CCIsSelected', '1')]]
  });

  fc = this.profileForm.controls;

  constructor(private _global: GlobalService,
      private fb: FormBuilder,
      private _person: PersonService,
      private _general: CommunicationService,
      private _security: SecurityService,
      private confirmationService: ConfirmationService) { }

  ngOnInit(): void {
    this.subscription = new Subscription();
    this.initializeForm();
  }

  initializeForm(): void {
    const personId = this._global.find((<any>_GlobalNS).paths.PersonId);
    this.states = this._general.getStateslabelValue();

    this._person.findById(personId).subscribe(response => {
      this.fc['Email'].setValue(<any>response.data[0].Email);
      this.fc['IsPreReg'].setValue(<any>response.data[0].IsPreReg);
      this.fc['FirstName'].setValue(<any>response.data[0].FirstName);
      this.fc['LastName'].setValue(<any>response.data[0].LastName);
      this.isPreReg = <any>response.data[0].IsPreReg;

      if (this.isPreReg) {
        this.fc['Password'].setValidators([Validators.required, Validators.minLength(8), Validators.maxLength(50), this.customValidator.passwordComplexity, this.customValidator.mustNotContain('Email'), this.customValidator.mustNotContain('FirstName'), this.customValidator.mustNotContain('LastName')]);
        this.fc['Question1'].setValidators([Validators.required]);
        this.fc['Question2'].setValidators([Validators.required, this.customValidator.notSameAs('Question1')]);
      }
    });

    this._general.getAddress(personId, 'Person').subscribe(response => {
      for (let i = 0; i < <any>response.data.length; i++) {
        if(<any>response.data[i].LocationType === 'Work'){
          this.fc[<any>response.data[i].LocationType + 'Address1'].setValue(<any>response.data[i].Address1);
          this.fc[<any>response.data[i].LocationType + 'Address2'].setValue(<any>response.data[i].Address2);
          this.fc[<any>response.data[i].LocationType + 'City'].setValue(<any>response.data[i].City);

          if (<any>response.data[i].State) {
            this.fc[<any>response.data[i].LocationType + 'State'].setValue(<any>response.data[i].State.toUpperCase());
          }

          if (<any>response.data[i].PostalCode) {
            let PostalCode = <any>response.data[i].PostalCode.toString();
            if (PostalCode.length < 5) {
              PostalCode = this.toZip(PostalCode);
            }
            this.fc[<any>response.data[i].LocationType + 'PostalCode'].setValue(PostalCode);
          }

          if (response.data[i].LocationType === 'Work') {
            this.fc['Company'].setValue(<any>response.data[i].Company);
          }
        }
      }
    });

    this._general.getphone(personId, 'Person').subscribe(responsePhone => {
      for (let j = 0; j < <any>responsePhone.data.length; j++) {
        if (responsePhone.data[j].Phone && responsePhone.data[j].LocationType ==='Work') {
          this.fc[responsePhone.data[j].LocationType + responsePhone.data[j].Type].setValue(responsePhone.data[j].Phone.toString());
        }
      }
    });

    this._person.findByEmailOption(personId).subscribe(response => {
      this.fc['Signature'].setValue(<any>response.data[0].Signature);
      this.fc['CCIsSelected'].setValue(<any>response.data[0].CC.IsSelected);
      this.fc['CCEmail'].setValue(<any>response.data[0].CC.Email);
      this.fc['OOOIsSelected'].setValue(<any>response.data[0].OOO.IsSelected);
      this.fc['Message'].setValue(<any>response.data[0].OOO.Message);
    });

    this._security.getQuestion().subscribe(response => {
      this.questionOptions = this.convertTolabelValue(<any>response.data);
    });
  }

  convertTolabelValue(Data: any): any {
    const response = [];
    for (let i = 0; i < Data.length; i++) {
      response[i] = {label: Data[i].Value, value: Data[i].Id};
    }
    return response;
  }

  toZip(val: string): string {
    const z = '0';
    return val.length >= 5 ? val : new Array(5 - val.length + 1).join(z) + val;
  }

  setValidationMessage(Key: string, CustomMessage?: string): void {
    const cntl = this.fc[Key];
    this.formErrors[Key] = '';

    const errorText = this.customValidator.message(cntl);

    if (errorText.length) {
      this.formErrors[Key] =  (!CustomMessage) ? errorText : CustomMessage;
    }
  }

  setHomeAddress(): void {
    if (this.fc['SameAsWork'].value) {
      const keys = ['Address1', 'Address2', 'City', 'State', 'PostalCode', 'Phone', 'Mobile', 'Fax'];

      for (let i = 0; i < keys.length; i++) {
        if (this.fc['Work' + keys[i]].value) {
          this.fc['Home' + keys[i]].setValue(this.fc['Work' + keys[i]].value);
        }
      }
    }
  }

  get personData(): any {
    return {
      PersonId: this.fc['PersonId'].value,
      FirstName: this.fc['FirstName'].value,
      LastName: this.fc['LastName'].value
    };
  }

  get phoneData(): any {
    return {
      ResourceId: this.fc['PersonId'].value,
      ResourceType: 'Person',
      Phone: [
        {
          Phone: this.fc['WorkPhone'].value,
          Type: 'Phone',
          LocationType: 'Work'
        },
        {
          Phone: this.fc['WorkMobile'].value,
          Type: 'Mobile', LocationType: 'Work' },
        {
          Phone: this.fc['WorkFax'].value,
          Type: 'Fax',
          LocationType: 'Work'
        }
        /*{
          Phone: this.fc['HomePhone'].value,
          Type: 'Phone',
          LocationType: 'Home'
        },
        {
          Phone: this.fc['HomeMobile'].value,
          Type: 'Mobile',
          LocationType: 'Home'
        },
        {
          Phone: this.fc['HomeFax'].value,
          Type: 'Fax',
          LocationType: 'Home'
        }*/
      ]
    };
  }

  get addressData(): any {
    return {
      ResourceId: this.fc['PersonId'].value,
      ResourceType: 'Person',
      Address: [
        {
          LocationType: 'Work',
          Address1: this.fc['WorkAddress1'].value,
          Address2: this.fc['WorkAddress2'].value,
          City: this.fc['WorkCity'].value,
          State: this.fc['WorkState'].value,
          PostalCode: this.fc['WorkPostalCode'].value,
          Company: this.fc['Company'].value
        }
        /*,
        {
          LocationType: 'Home',
          Address1: this.fc['HomeAddress1'].value,
          Address2: this.fc['HomeAddress2'].value,
          City: this.fc['HomeCity'].value,
          State: this.fc['HomeState'].value,
          PostalCode: this.fc['HomePostalCode'].value
        }*/
      ]
    };
  }

  get emailOptionsData(): any {
    return {
      PersonId: this.fc['PersonId'].value,
      EmailOptions: {
        Signature: this.fc['Signature'].value,
        OOO: {
          IsSelected: this.fc['OOOIsSelected'].value === '1' ? true : false,
          Message: this.fc['Message'].value
        },
        CC: {
          IsSelected: this.fc['CCIsSelected'].value === '1' ? true : false,
          Email: this.fc['CCEmail'].value
        }
      }
    };
  }

  get SecurityQuestionData(): any {
    const Answers = [
      {
        QuestionId: this.fc['Question1'].value.toString(),
        Answer: this.fc['Answer1'].value
      },
      {
        QuestionId: this.fc['Question2'].value.toString(),
        Answer: this.fc['Answer2'].value
      }
    ];

    return { Answers };
  }

  onSubmit(): void {
    for (const key of Object.keys(this.fc)) {
      this.fc[key].markAsDirty();
      this.fc[key].updateValueAndValidity();
      this.setValidationMessage(key);
    }

    if (this.profileForm.valid) {
      this.confirmationService.confirm({
        message: 'Are you sure you want to make the changes?',
        accept: () => {
          this.loading = true;
          this.messages = [{severity: 'info', summary: 'Updating', detail: ''}];
          this.updateUserData();
        }
      });
    }
  }

  updateUserData(): void {
    forkJoin(
      this._person.update(this.personData),
      this._security.updateQuestion(this.SecurityQuestionData),
      this._security.updatePassword(this.fc['Password'].value),
      this._general.updateAddress(this.addressData),
      this._general.updatePhone(this.phoneData),
      this._person.updateEmailOption(this.emailOptionsData)
    ).subscribe(([person, question, password, address, phone, email]) => {
      if(password.error) {
        if (password.messages.length) {
          this.messages = [{severity: 'error', summary: password.messages[0], detail: ''}];
        } else {
          this.messages = [{severity: 'error', summary: 'Update Failed! Please contact administrator', detail: ''}];
        }
        this.loading = false;
      } else {
        this.messages = [{severity: 'success', summary: 'Updated', detail: ''}];
        this.loading = false;
        if (this.isPreReg) {
          this.rejectConfirmation = false;
          this.confirmationService.confirm({
            message: 'You will be taken to the EQ Connect Login page. Use your email as your Username, and your newly created Password to log in',
            accept: () => {
              this._global.document.location.href = '/';
            }
          });
        }
      }
    }, error => {
      if (error.statusText) {
        this.messages = [{severity: 'error', summary: error.statusText, detail: ''}];
      } else {
        this.messages = [{severity: 'error', summary: 'Update Failed! Please contact administrator', detail: ''}];
      }
      this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
